import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Box, Button, Typography, IconButton } from '@mui/material';
import { commonFileUpload } from '../../redux/features/assetManagementSlice';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CircularProgress from '@mui/material/CircularProgress';
import { Icon } from '@iconify/react/dist/iconify.js';
import GlobalViewAttachment from './GlobalViewAttachment';

const GlobalFileUploadComponent = ({
  formik,
  fileDisplayName,
  fileBackendLink,
  accept = '.pdf,image/*',
  label = 'Attachment',
  previewButtonName,
  removeButtonName,
  updateLabel = 'update',
}) => {
  const { fileUploadLoading } = useSelector((store) => store.assetmanagement);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const uploadFile = async (file) => {
    try {
      const formData = new FormData();
      formData.append('uploads', file);

      const response = await dispatch(commonFileUpload(formData)).unwrap();
      if (response?.status !== 'success' || !response?.data?.filename) {
        throw new Error(response?.message || 'File upload failed');
      }
      return response?.data?.filename || '';
    } catch (error) {
      console.log('Upload error:', error);
      enqueueSnackbar(error?.message || 'File upload failed', { variant: 'error' });
      throw error;
    }
  };

  const handleRemove = () => {
    if (fileUploadLoading) return;
    formik.setFieldValue(fileBackendLink, '');
    if (fileDisplayName) {
      formik.setFieldValue(fileDisplayName, '');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
      {/* Upload / Change + Remove */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          variant="outlined"
          component="label"
          size="small"
          disabled={fileUploadLoading}
          startIcon={
            fileUploadLoading ? (
              <CircularProgress size={16} />
            ) : formik.values[fileBackendLink] ? (
              <SwapHorizIcon fontSize="small" />
            ) : (
              <AttachFileIcon fontSize="small" />
            )
          }
          sx={{
            width: '100%',
            textTransform: 'none',
            borderStyle: 'dashed',
            borderRadius: 2,
            px: 2,
            minHeight: 36,
            fontWeight: 500,
            justifyContent: 'flex-start',
            '&:hover': {
              borderStyle: 'solid',
            },
          }}
        >
          {formik.values[fileBackendLink] ? (updateLabel ?? 'Update') : label}

          <input
            type="file"
            hidden
            accept={accept}
            disabled={fileUploadLoading}
            onChange={async (e) => {
              const file = e.target.files[0];
              if (!file) return;

              try {
                const uploadedFile = await uploadFile(file);

                formik.setFieldValue(fileBackendLink, uploadedFile);
                if (fileDisplayName) {
                  formik.setFieldValue(fileDisplayName, file.name);
                }
              } finally {
                e.target.value = null;
              }
            }}
          />
        </Button>
        {formik.values[fileBackendLink] && (
          <GlobalViewAttachment
            attachmentFileName={formik.values[fileBackendLink]}
            previewButtonName={previewButtonName}
          />
        )}

        {/* Remove button */}
        {formik.values[fileBackendLink] && (
          <Button disabled={fileUploadLoading} size="small" color="error" variant="outlined" onClick={handleRemove}>
            <Icon icon="mdi:remove-bold" width="20" height="18" /> {removeButtonName || ''}
          </Button>
        )}
      </Box>

      {/* File name */}
      {formik.values[fileBackendLink] && fileDisplayName && (
        <Typography
          variant="caption"
          sx={{
            ml: 1,
            display: 'flex',
            alignItems: 'center',
            fontStyle: 'italic',
            gap: 0.5,
            opacity: fileUploadLoading ? 0.6 : 1,
          }}
        >
          {formik.values[fileDisplayName]}
        </Typography>
      )}
    </Box>
  );
};

export default GlobalFileUploadComponent;
